const toNumber = (value, fallback) => {
  if (typeof value === 'number') return value;
  const parsed = Number.parseInt(value, 10);
  return Number.isNaN(parsed) ? fallback : parsed;
};

export const TOUR_LOCALE = {
  back: 'Atrás',
  close: 'Cerrar',
  last: 'Finalizar',
  next: 'Siguiente',
  skip: 'Omitir',
};

export const buildTourLocale = (t) => ({
  back: t('tour.back'),
  close: t('tour.close'),
  last: t('tour.last'),
  next: t('tour.next'),
  skip: t('tour.skip'),
});

export const createTourStyles = (theme) => ({
  options: {
    arrowColor: theme.palette.background.paper,
    backgroundColor: theme.palette.background.paper,
    overlayColor: 'rgba(15, 23, 42, 0.65)',
    primaryColor: theme.palette.primary.main,
    textColor: theme.palette.text.primary,
    width: 420,
    zIndex: 10000,
  },
  buttonBack: {
    backgroundColor: theme.palette.action.hover,
    border: `1.5px solid ${theme.palette.divider}`,
    borderRadius: 999,
    color: theme.palette.text.primary,
    fontSize: '0.85rem',
    fontWeight: 700,
    marginRight: 12,
    outline: 'none',
    padding: '8px 22px',
  },
  buttonClose: {
    color: theme.palette.text.secondary,
    height: 14,
    padding: 10,
    position: 'absolute',
    right: 8,
    top: 8,
    width: 14,
  },
  buttonNext: {
    backgroundColor: theme.palette.primary.main,
    border: 'none',
    borderRadius: 999,
    color: theme.palette.primary.contrastText,
    fontSize: '0.85rem',
    fontWeight: 800,
    outline: 'none',
    padding: '8px 28px',
  },
  buttonSkip: {
    backgroundColor: 'transparent',
    border: 'none',
    color: theme.palette.text.secondary,
    fontSize: '0.8rem',
    fontWeight: 500,
  },
  tooltip: {
    backgroundColor: theme.palette.background.paper,
    borderRadius: 16,
    boxShadow: theme.shadows[12],
    color: theme.palette.text.primary,
    padding: '24px 28px 20px',
  },
  tooltipContainer: {
    textAlign: 'left',
  },
  tooltipContent: {
    color: theme.palette.text.secondary,
    fontSize: '0.9rem',
    lineHeight: 1.75,
    padding: '8px 0 20px',
  },
  tooltipTitle: {
    borderBottom: `2px solid ${theme.palette.primary.main}`,
    color: theme.palette.text.primary,
    display: 'inline-block',
    fontSize: '1.15rem',
    fontWeight: 800,
    marginBottom: 4,
    padding: '0 0 10px',
  },
  tooltipFooter: {
    marginTop: 4,
  },
  spotlight: {
    borderRadius: 12,
  },
});

export const filterAvailableTourSteps = (steps = []) => {
  if (typeof document === 'undefined') {
    return steps;
  }

  return steps
    .filter((step) => {
      if (!step?.target || step.target === 'body' || step.target === 'html') {
        return true;
      }

      try {
        return Boolean(document.querySelector(step.target));
      } catch {
        // A malformed selector must not prevent help from opening.
        return false;
      }
    })
    .map((step) => ({ ...step, disableBeacon: true }));
};

export const WELCOME_TOUR_STEPS = [
  {
    target: '[data-tour="dashboard-header"]',
    titleKey: 'welcomeTour.steps.dashboardHeader.title',
    contentKey: 'welcomeTour.steps.dashboardHeader.content',
    title: 'Tu panel principal',
    content: 'Este dashboard es el punto de partida para entrar rápido en los módulos clave y revisar el estado general de la temporada.',
    disableBeacon: true,
    placement: 'bottom',
  },
  {
    target: '[data-tour="dashboard-stats"]',
    titleKey: 'welcomeTour.steps.dashboardStats.title',
    contentKey: 'welcomeTour.steps.dashboardStats.content',
    title: 'Contexto de la temporada',
    content: 'Aquí ves el contexto global de trabajo antes de pasar a análisis más concretos por partidos, jugadores o equipos.',
    placement: 'bottom',
  },
  {
    target: '[data-tour="dashboard-stats-card"]',
    titleKey: 'welcomeTour.steps.dashboardStatsCard.title',
    contentKey: 'welcomeTour.steps.dashboardStatsCard.content',
    title: 'Estadísticas',
    content: 'Entra a Estadísticas de Jugadores y Equipos para buscar perfiles, filtrar temporadas y exportar informes listos para compartir.',
    placement: 'bottom',
  },
  {
    target: '[data-tour="dashboard-ranking-card"]',
    titleKey: 'welcomeTour.steps.dashboardRankingCard.title',
    contentKey: 'welcomeTour.steps.dashboardRankingCard.content',
    title: 'Rankings',
    content: 'Los rankings te permiten comparar rendimiento con filtros, cambios de vista y exportación rápida a Excel.',
    placement: 'bottom',
  },
  {
    target: '[data-tour="dashboard-scouting-card"]',
    titleKey: 'welcomeTour.steps.dashboardScoutingCard.title',
    contentKey: 'welcomeTour.steps.dashboardScoutingCard.content',
    title: 'Scouting',
    content: 'Sigue jugadores, revisa agendas, guarda notas y organiza tu observación desde una sola pantalla.',
    placement: 'bottom',
  },
  {
    target: '[data-tour="dashboard-live-tag-card"]',
    titleKey: 'welcomeTour.steps.dashboardLiveTagCard.title',
    contentKey: 'welcomeTour.steps.dashboardLiveTagCard.content',
    title: 'Live Tag',
    content: 'Accede al flujo de etiquetado en vivo para capturar eventos y sesiones durante el partido.',
    placement: 'bottom',
  },
  {
    target: 'body',
    titleKey: 'welcomeTour.steps.matchReport.title',
    contentKey: 'welcomeTour.steps.matchReport.content',
    title: 'Informe de Partido',
    content: 'El Informe de Partido es la vista de análisis previo al encuentro, con contexto del rival, clima, secciones detalladas y exportación a PowerPoint.',
    placement: 'center',
  },
  {
    target: '[data-tour="header-tour-trigger"]',
    titleKey: 'welcomeTour.steps.tourTrigger.title',
    contentKey: 'welcomeTour.steps.tourTrigger.content',
    title: '¿Quieres repetirlo?',
    content: 'Este botón del header abre la ayuda contextual y te permite relanzar el onboarding cuando quieras.',
    placement: 'bottom',
  },
];

export const PAGE_TOUR_STEPS = {
  dashboard: [
    {
      target: '[data-tour="dashboard-header"]',
      titleKey: 'pageTour.dashboard.header.title',
      contentKey: 'pageTour.dashboard.header.content',
      title: 'Dashboard',
      content: 'Resumen rápido del entorno de trabajo con accesos directos y estado general del usuario.',
      disableBeacon: true,
      placement: 'bottom',
    },
    {
      target: '[data-tour="dashboard-stats"]',
      titleKey: 'pageTour.dashboard.stats.title',
      contentKey: 'pageTour.dashboard.stats.content',
      title: 'Indicadores clave',
      content: 'Estas tarjetas resumen la temporada disponible y el contexto base con el que trabaja la app.',
      placement: 'bottom',
    },
    {
      target: '[data-tour="dashboard-quick-actions"]',
      titleKey: 'pageTour.dashboard.quickActions.title',
      contentKey: 'pageTour.dashboard.quickActions.content',
      title: 'Accesos rápidos',
      content: 'Desde aquí navegas directamente a clasificación, estadísticas, rankings, scouting, live tag y otros módulos frecuentes.',
      placement: 'top',
    },
    {
      target: '[data-tour="dashboard-tips"]',
      titleKey: 'pageTour.dashboard.tips.title',
      contentKey: 'pageTour.dashboard.tips.content',
      title: 'Consejos prácticos',
      content: 'Este bloque recuerda buenas prácticas de uso y ayuda a orientar a nuevos usuarios.',
      placement: 'top',
    },
  ],
  estadisticasJugadores: [
    {
      target: '[data-tour="player-stats-header"]',
      titleKey: 'pageTour.estadisticasJugadores.header.title',
      contentKey: 'pageTour.estadisticasJugadores.header.content',
      title: 'Estadísticas de Jugadores',
      content: 'Pantalla para analizar el perfil de un jugador concreto y mantener el foco del scouting.',
      disableBeacon: true,
      placement: 'bottom',
    },
    {
      target: '[data-tour="player-stats-search"]',
      titleKey: 'pageTour.estadisticasJugadores.search.title',
      contentKey: 'pageTour.estadisticasJugadores.search.content',
      title: 'Buscar jugador',
      content: 'Empieza buscando un jugador o reabriendo el buscador para cambiar el foco del análisis.',
      placement: 'bottom',
    },
    {
      target: '[data-tour="player-stats-filters"]',
      titleKey: 'pageTour.estadisticasJugadores.filters.title',
      contentKey: 'pageTour.estadisticasJugadores.filters.content',
      title: 'Filtros por temporada',
      content: 'Filtra temporadas para centrar el análisis y comparar periodos concretos del jugador.',
      placement: 'bottom',
    },
    {
      target: '[data-tour="player-stats-table"]',
      titleKey: 'pageTour.estadisticasJugadores.table.title',
      contentKey: 'pageTour.estadisticasJugadores.table.content',
      title: 'Datos detallados',
      content: 'Aquí ves totales, desglose y tabla de estadísticas para profundizar en rendimiento y contexto competitivo.',
      placement: 'top',
    },
    {
      target: '[data-tour="player-stats-export"]',
      titleKey: 'pageTour.estadisticasJugadores.export.title',
      contentKey: 'pageTour.estadisticasJugadores.export.content',
      title: 'Exportar',
      content: 'Cuando el jugador está cargado puedes exportar la vista actual para compartirla fuera de la app.',
      placement: 'top',
    },
  ],
  estadisticasEquipos: [
    {
      target: '[data-tour="team-stats-header"]',
      titleKey: 'pageTour.estadisticasEquipos.header.title',
      contentKey: 'pageTour.estadisticasEquipos.header.content',
      title: 'Estadísticas de Equipos',
      content: 'Pantalla para revisar el rendimiento colectivo del equipo a través de varias vistas.',
      disableBeacon: true,
      placement: 'bottom',
    },
    {
      target: '[data-tour="team-stats-search"]',
      titleKey: 'pageTour.estadisticasEquipos.search.title',
      contentKey: 'pageTour.estadisticasEquipos.search.content',
      title: 'Buscar equipo',
      content: 'Selecciona el equipo que quieres analizar y cambia rápidamente a otro cuando lo necesites.',
      placement: 'bottom',
    },
    {
      target: '[data-tour="team-stats-tabs"]',
      titleKey: 'pageTour.estadisticasEquipos.tabs.title',
      contentKey: 'pageTour.estadisticasEquipos.tabs.content',
      title: 'Resumen, jugadores y partidos',
      content: 'Las pestañas separan resumen global, rendimiento de jugadores, goles y partidos para explorar cada capa de datos.',
      placement: 'top',
    },
    {
      target: '[data-tour="team-stats-export"]',
      titleKey: 'pageTour.estadisticasEquipos.export.title',
      contentKey: 'pageTour.estadisticasEquipos.export.content',
      title: 'Exportación rápida',
      content: 'Exporta la vista del equipo cuando ya tengas seleccionado el caso que quieres compartir.',
      placement: 'top',
    },
  ],
  rankingJugadores: [
    {
      target: '[data-tour="ranking-players-header"]',
      titleKey: 'pageTour.rankingJugadores.header.title',
      contentKey: 'pageTour.rankingJugadores.header.content',
      title: 'Ranking de Jugadores',
      content: 'Compara jugadores con vista tabla o cards según el tipo de lectura que prefieras.',
      disableBeacon: true,
      placement: 'bottom',
    },
    {
      target: '[data-tour="ranking-players-filters"]',
      titleKey: 'pageTour.rankingJugadores.filters.title',
      contentKey: 'pageTour.rankingJugadores.filters.content',
      title: 'Filtros globales',
      content: 'Usa los filtros de temporada, competición y grupo para recalcular el ranking sobre el contexto adecuado.',
      placement: 'bottom',
    },
    {
      target: '[data-tour="ranking-players-search"]',
      titleKey: 'pageTour.rankingJugadores.search.title',
      contentKey: 'pageTour.rankingJugadores.search.content',
      title: 'Búsqueda y refino',
      content: 'Puedes buscar, limitar por equipo o posición y cambiar rápidamente de categoría de análisis.',
      placement: 'top',
    },
    {
      target: '[data-tour="ranking-players-chart"]',
      titleKey: 'pageTour.rankingJugadores.chart.title',
      contentKey: 'pageTour.rankingJugadores.chart.content',
      title: 'Top visual',
      content: 'El gráfico superior resume el top de la métrica activa para detectar tendencias de un vistazo.',
      placement: 'top',
    },
    {
      target: '[data-tour="ranking-players-export"]',
      titleKey: 'pageTour.rankingJugadores.export.title',
      contentKey: 'pageTour.rankingJugadores.export.content',
      title: 'Exportar a Excel',
      content: 'Exporta el ranking filtrado para compartirlo o trabajarlo fuera de la aplicación.',
      placement: 'bottom',
    },
  ],
  rankingEquipos: [
    {
      target: '[data-tour="ranking-teams-header"]',
      titleKey: 'pageTour.rankingEquipos.header.title',
      contentKey: 'pageTour.rankingEquipos.header.content',
      title: 'Ranking de Equipos',
      content: 'Aquí comparas equipos con métricas globales, cambios de vista y orden dinámico.',
      disableBeacon: true,
      placement: 'bottom',
    },
    {
      target: '[data-tour="ranking-teams-filters"]',
      titleKey: 'pageTour.rankingEquipos.filters.title',
      contentKey: 'pageTour.rankingEquipos.filters.content',
      title: 'Filtros principales',
      content: 'Los filtros globales controlan qué temporada y grupo alimentan el ranking.',
      placement: 'bottom',
    },
    {
      target: '[data-tour="ranking-teams-search"]',
      titleKey: 'pageTour.rankingEquipos.search.title',
      contentKey: 'pageTour.rankingEquipos.search.content',
      title: 'Búsqueda y categorías',
      content: 'Filtra por nombre y usa categorías rápidas para enfocar ataque, defensa o clasificación general.',
      placement: 'top',
    },
    {
      target: '[data-tour="ranking-teams-chart"]',
      titleKey: 'pageTour.rankingEquipos.chart.title',
      contentKey: 'pageTour.rankingEquipos.chart.content',
      title: 'Top visual de equipos',
      content: 'Este bloque resume los mejores equipos según la métrica activa para una lectura rápida.',
      placement: 'top',
    },
    {
      target: '[data-tour="ranking-teams-results"]',
      titleKey: 'pageTour.rankingEquipos.results.title',
      contentKey: 'pageTour.rankingEquipos.results.content',
      title: 'Resultados y detalle',
      content: 'La parte inferior concentra la tabla o las cards con el detalle completo del ranking.',
      placement: 'top',
    },
  ],
  scouting: [
    {
      target: '[data-tour="scouting-header"]',
      titleKey: 'pageTour.scouting.header.title',
      contentKey: 'pageTour.scouting.header.content',
      title: 'Scouting',
      content: 'Pantalla central para seguimiento de jugadores, partidos, agenda y notas.',
      disableBeacon: true,
      placement: 'bottom',
    },
    {
      target: '[data-tour="scouting-tabs"]',
      titleKey: 'pageTour.scouting.tabs.title',
      contentKey: 'pageTour.scouting.tabs.content',
      title: 'Flujo por pestañas',
      content: 'Las pestañas separan jugadores seguidos, agenda, partidos jugados, agenda personal y notas de partido.',
      placement: 'bottom',
    },
    {
      target: '[data-tour="scouting-search"]',
      titleKey: 'pageTour.scouting.search.title',
      contentKey: 'pageTour.scouting.search.content',
      title: 'Seguimiento activo',
      content: 'Busca jugadores o equipos dentro de tus seguidos para trabajar más rápido cuando la lista crece.',
      placement: 'bottom',
    },
    {
      target: '[data-tour="scouting-agenda"]',
      titleKey: 'pageTour.scouting.agenda.title',
      contentKey: 'pageTour.scouting.agenda.content',
      title: 'Agenda de partidos',
      content: 'Aquí revisas próximos partidos, filtras por jugador y accedes a actas, notas o guardado en agenda.',
      placement: 'top',
    },
    {
      target: '[data-tour="scouting-personal-agenda"]',
      titleKey: 'pageTour.scouting.personalAgenda.title',
      contentKey: 'pageTour.scouting.personalAgenda.content',
      title: 'Mi agenda',
      content: 'La agenda personal reúne los partidos marcados para organizar la observación semanal.',
      placement: 'top',
    },
  ],
  informePartido: [
    {
      target: '[data-tour="report-header"]',
      titleKey: 'pageTour.informePartido.header.title',
      contentKey: 'pageTour.informePartido.header.content',
      title: 'Informe de Partido',
      content: 'Esta página concentra el análisis previo de un partido con foco en rival, contexto y exportación.',
      disableBeacon: true,
      placement: 'bottom',
    },
    {
      target: '[data-tour="report-match-card"]',
      titleKey: 'pageTour.informePartido.matchCard.title',
      contentKey: 'pageTour.informePartido.matchCard.content',
      title: 'Contexto del encuentro',
      content: 'La cabecera muestra fecha, sede, clima y los dos equipos para situarte rápidamente.',
      placement: 'bottom',
    },
    {
      target: '[data-tour="report-team-selection"]',
      titleKey: 'pageTour.informePartido.teamSelection.title',
      contentKey: 'pageTour.informePartido.teamSelection.content',
      title: 'Selecciona el equipo a estudiar',
      content: 'Elige local o visitante para cargar todas las secciones del informe con sus datos asociados.',
      placement: 'bottom',
    },
    {
      target: '[data-tour="report-export"]',
      titleKey: 'pageTour.informePartido.export.title',
      contentKey: 'pageTour.informePartido.export.content',
      title: 'Exportar a PowerPoint',
      content: 'Cuando el equipo está seleccionado puedes generar la exportación PPTX desde aquí.',
      placement: 'bottom',
    },
    {
      target: '[data-tour="report-analysis-content"]',
      titleKey: 'pageTour.informePartido.analysisContent.title',
      contentKey: 'pageTour.informePartido.analysisContent.content',
      title: 'Secciones del informe',
      content: 'Tras cargar datos aparecen resumen ejecutivo, contexto, tablas y bloques analíticos para profundizar en el rival.',
      placement: 'top',
    },
  ],
};


export const getAvailablePageTourSteps = (pageKey) =>
  filterAvailableTourSteps(PAGE_TOUR_STEPS[pageKey] || []);

// Routes that do not expose stable, page-specific controls use a deliberate
// page-level body step. This keeps help useful without pointing Joyride at a
// selector that may be absent because of loading, permissions, or empty data.
const CONTEXTUAL_PAGE_TOUR_KEYS = [
  'landing',
  'login',
  'register',
  'forgotPassword',
  'resetPassword',
  'acceptInvitation',
  'profile',
  'manageTeams',
  'windowsClient',
  'adminUsers',
  'homeLegacy',
  'matches',
  'matchSheet',
  'playerProfile',
  'standings',
  'clubs',
  'tools',
  'individualAnalysis',
  'individualAnalysisDetail',
  'chatAgent',
  'planning',
  'liveTag',
  'liveTagSession',
  'videoTagSession',
  'licenses',
  'licenseManagement',
  'adminAnalytics',
  'adminLicenses',
  'adminPromoCodes',
  'appVersions',
  'featureFlags',
  'unauthorized',
  'designSystemDemo',
];

CONTEXTUAL_PAGE_TOUR_KEYS.forEach((pageKey) => {
  PAGE_TOUR_STEPS[pageKey] = [
    {
      target: 'body',
      titleKey: `pageTour.${pageKey}.overview.title`,
      contentKey: `pageTour.${pageKey}.overview.content`,
      disableBeacon: true,
      placement: 'center',
    },
  ];
});

const EXACT_ROUTE_TOUR_KEYS = {
  '/': 'landing',
  '/login': 'login',
  '/register': 'register',
  '/forgot-password': 'forgotPassword',
  '/reset-password': 'resetPassword',
  '/perfil': 'profile',
  '/administrar-equipos': 'manageTeams',
  '/cliente-windows': 'windowsClient',
  '/admin/usuarios': 'adminUsers',
  '/home-old': 'homeLegacy',
  '/dashboard': 'dashboard',
  '/partidos': 'matches',
  '/rankingJugadores': 'rankingJugadores',
  '/actaPartido': 'matchSheet',
  '/estadisticas': 'estadisticasJugadores',
  '/jugador': 'playerProfile',
  '/clasificacion': 'standings',
  '/rankingEquipos': 'rankingEquipos',
  '/estadisticasEquipos': 'estadisticasEquipos',
  '/clubes': 'clubs',
  '/herramientas': 'tools',
  '/informePartido': 'informePartido',
  '/analisisIndividual': 'individualAnalysis',
  '/agente': 'chatAgent',
  '/scouting': 'scouting',
  '/planificacion': 'planning',
  '/live-tag': 'liveTag',
  '/licencias': 'licenses',
  '/mis-licencias': 'licenseManagement',
  '/admin/analytics': 'adminAnalytics',
  '/admin/licencias': 'adminLicenses',
  '/admin/promo-codes': 'adminPromoCodes',
  '/admin/versiones': 'appVersions',
  '/admin/feature-flags': 'featureFlags',
  '/unauthorized': 'unauthorized',
  '/design-demo': 'designSystemDemo',
};

export const resolvePageTourKey = (pathname = '') => {
  if (pathname.startsWith('/invitaciones/')) return 'acceptInvitation';
  if (pathname.startsWith('/analisisIndividual/microconcepto/')) return 'individualAnalysisDetail';
  if (pathname.startsWith('/live-tag/session/') && pathname.endsWith('/video')) return 'videoTagSession';
  if (pathname.startsWith('/live-tag/session/')) return 'liveTagSession';

  return EXACT_ROUTE_TOUR_KEYS[pathname] || null;
};

export const buildTranslatedSteps = (steps, t) =>
  steps.map((step) => ({
    ...step,
    title: step.titleKey ? t(step.titleKey) : step.title,
    content: step.contentKey ? t(step.contentKey) : step.content,
  }));
